// Shortcut management module for PPT Hero game
import ShortcutsConfig from '../config/ShortcutsConfig.js';

export class ShortcutManager {
  constructor(scene, firmType = 'GENERIC') {
    this.scene = scene;
    this.firmType = firmType;
    this.shortcutSequence = [];
    this.lastKeyTime = 0;
    this.sequenceTimeout = 3000; // ms before resetting multi-key combos
    this.sequenceTimer = null;
    
    // Note: setupKeyboardListener() will be called from scene.setupInput()
  }

  // Get all shortcuts for current firm
  getShortcuts() {
    const baseShortcuts = {};
    const firmShortcuts = ShortcutsConfig.FIRM_SHORTCUTS[this.firmType] || {};

    // Convert base shortcuts to executable format
    Object.entries(ShortcutsConfig.BASE_SHORTCUTS).forEach(([key, config]) => {
      baseShortcuts[key] = () => this.scene[config.action]();
    });

    // Convert firm shortcuts to executable format
    const executableFirmShortcuts = {};
    Object.entries(firmShortcuts).forEach(([key, config]) => {
      executableFirmShortcuts[key] = () => this.scene[config.action]();
    });

    return { ...baseShortcuts, ...executableFirmShortcuts };
  }

  // Get human-readable shortcut description
  getShortcutDescription(shortcutKey) {
    const descriptions = {
      "alt,h,g,a,l": "Align Left",
      "alt,h,g,a,r": "Align Right", 
      "alt,h,g,a,t": "Align Top",
      "alt,h,g,a,b": "Align Bottom",
      "alt,h,g,a,c": "Center Horizontally",
      "alt,h,g,a,m": "Center Vertically",
      "alt,h,g,a,h": "Distribute Horizontally",
      "alt,h,g,a,v": "Distribute Vertically",
      "alt,b,r,a,p,i,d": "RAPID Framework",
      "alt,b,n,p,s": "NPS Analysis",
      "alt,b,c,e,o": "CEO Dashboard",
      "alt,b,f,i,v,e": "Five Forces",
      "alt,b,v,a,l,u,e": "Value Chain",
      "alt,b,s,w,o,t": "SWOT Analysis"
    };
    
    return descriptions[shortcutKey] || shortcutKey;
  }

  // Get shortcut descriptions for display (with correct sequential formatting)
  getShortcutDescriptions() {
    return {
      'Ctrl+A': 'Select All',
      'Alt,H,G,A,L': 'Align Left',
      'Alt,H,G,A,R': 'Align Right',
      'Alt,H,G,A,T': 'Align Top',
      'Alt,H,G,A,B': 'Align Bottom',
      'Alt,H,G,A,C': 'Center Horizontally',
      'Alt,H,G,A,M': 'Center Vertically',
      'Alt,H,G,A,H': 'Distribute Horizontally',
      'Alt,H,G,A,V': 'Distribute Vertically',
      'Ctrl+Shift+[': 'Send to Back',
      'Ctrl+Shift+]': 'Bring to Front'
    };
  }

  // Create shortcuts help popup using proper Rex UI Dialog
  createShortcutsPopup() {
    try {
      // Remove existing popup if any
      if (this.scene.shortcutsPopup) {
        this.scene.shortcutsPopup.destroy();
      }
      
      // Get shortcuts
      const shortcuts = this.scene.getShortcutDescriptions ? 
        this.scene.getShortcutDescriptions() : 
        this.getShortcutDescriptions();
      
      // Create content text
      let contentText = '';
      Object.entries(shortcuts).forEach(([shortcut, description]) => {
        if (shortcut.startsWith('---')) {
          contentText += `\n[b][color=#00d4ff]${shortcut}[/color][/b]\n`;
        } else {
          contentText += `[color=#00ff88]${shortcut}[/color] - [color=#ffffff]${description}[/color]\n`;
        }
      });
      
      // Create Rex UI dialog
      this.scene.shortcutsPopup = this.scene.rexUI.add.dialog({
        x: this.scene.cameras.main.width / 2,
        y: this.scene.cameras.main.height / 2,
        width: 600,
        height: 500,
        
        background: this.scene.rexUI.add.roundRectangle(0, 0, 2, 2, 12, 0x1a252f, 0x00d4ff, 3),
        
        title: this.scene.rexUI.add.label({
          background: this.scene.rexUI.add.roundRectangle(0, 0, 2, 2, 8, 0x2c3e50),
          text: this.scene.rexUI.add.BBCodeText(0, 0, '[b][color=#ffffff]Available Shortcuts[/color][/b]', {
            fontSize: '20px',
            fontFamily: 'Arial, sans-serif'
          }),
          space: { left: 20, right: 20, top: 12, bottom: 12 }
        }),
        
        content: this.scene.rexUI.add.BBCodeText(0, 0, contentText, {
          fontSize: '14px',
          fontFamily: 'Arial, sans-serif',
          wordWrap: { width: 550 },
          maxLines: 20
        }),
        
        actions: [
          this.scene.rexUI.add.label({
            background: this.scene.rexUI.add.roundRectangle(0, 0, 2, 2, 8, 0xe74c3c),
            text: this.scene.rexUI.add.BBCodeText(0, 0, '[b][color=#ffffff]Close[/color][/b]', {
              fontSize: '16px',
              fontFamily: 'Arial, sans-serif'
            }),
            space: { left: 20, right: 20, top: 10, bottom: 10 }
          })
        ],
        
        space: {
          title: 20,
          content: 20,
          action: 15,
          left: 20,
          right: 20,
          top: 20,
          bottom: 20
        }
      });
      
      this.scene.shortcutsPopup.layout();
      
      this.scene.shortcutsPopup.on('button.click', () => {
            this.scene.shortcutsPopup.destroy();
            this.scene.shortcutsPopup = null;
        this.scene.isTransitioning = false;
      });
      
      console.log('✅ Rex UI shortcuts dialog created with', Object.keys(shortcuts).length, 'shortcuts');
      
    } catch (error) {
      console.error('Error creating shortcuts popup:', error);
      this.scene.isTransitioning = false;
    }
  }

  // Setup comprehensive keyboard listener with all improvements
  setupKeyboardListener() {
    this.scene.input.keyboard.on("keydown", (event) => {
      console.log('🔧 Keyboard event:', event.key, 'gameState:', this.scene.gameStateManager?.getState(), 'inputEnabled:', this.scene.gameStateManager?.isInputEnabled());

      // ALWAYS prevent browser defaults for game shortcuts (regardless of game state)
      if (event.key === "Alt" ||
          (event.ctrlKey && event.key.toLowerCase() === "a") ||
          (event.ctrlKey && event.shiftKey && (event.key === "[" || event.key === "]"))) {
        event.preventDefault();
        event.stopPropagation();
        console.log('🛡️ Browser default prevented for:', event.key);
      }

      // Handle Ctrl+A immediately regardless of game state (if shapes exist)
      if (event.ctrlKey && event.key.toLowerCase() === "a") {
        console.log('🎯 Ctrl+A detected - checking if shapes exist');
        if (this.scene.allShapes && this.scene.allShapes.length > 0) {
          console.log('🎯 Shapes exist, calling selectAllShapes');
          this.selectAllShapes(); // Use ShortcutManager's own method with red borders
          console.log("✅ Executed shortcut: CTRL+A (Select All)");
        } else {
          console.log('❌ No shapes to select');
        }
        return;
      }

      // Process sequential shortcuts when shapes are available and "GO!" has been shown
      if (!this.scene.gameStateManager?.areShortcutsEnabled()) {
        console.log('❌ Sequential shortcuts blocked - wrong state or GO! not shown');
        return;
      }

      // Also check if shapes exist for sequential shortcuts to work
      if (!this.scene.shapeManager || this.scene.shapeManager.getAllShapes().length === 0) {
        console.log('❌ Sequential shortcuts blocked - no shapes available');
        return;
      }

      console.log('✅ Processing sequential shortcuts');

      // Handle shortcuts using improved system
      this.handleShortcuts(event);
    });
  }

  // Handle keyboard shortcuts with improved logic
  handleShortcuts(event) {
    // Check if event and event.key exist
    if (!event || !event.key) return;
  
    // Debug log key presses
    console.log(`Key pressed: ${event.key}, Alt: ${event.altKey}, Ctrl: ${event.ctrlKey}, Shift: ${event.shiftKey}`);

    // ALWAYS prevent browser defaults for game shortcuts (regardless of game state)
    if (event.key === "Alt" ||
        (event.ctrlKey && event.key.toLowerCase() === "a") ||
        (event.ctrlKey && event.shiftKey && (event.key === "[" || event.key === "]"))) {
      event.preventDefault();
      event.stopPropagation();
      console.log('🛡️ Browser default prevented for:', event.key);
    }

        // Handle immediate CTRL+SHIFT combinations (like Bain shortcuts) FIRST - before any other CTRL logic
    if (event.ctrlKey && event.shiftKey && event.key.length === 1) {
      const key = event.key.toLowerCase();
      const shortcutKey = `ctrl,shift,${key}`;
      const shortcuts = this.getShortcuts();
      
      console.log(`🔍 Checking for CTRL+SHIFT shortcut: ${shortcutKey}`);
      console.log(`🔍 Available shortcuts:`, Object.keys(shortcuts));
      

      
      if (shortcuts[shortcutKey]) {
        console.log(`🎯 Executing immediate CTRL+SHIFT shortcut: ${shortcutKey}`);
        shortcuts[shortcutKey]();
        this.trackShortcutUsed(shortcutKey);
        return;
      } else {
        console.log(`❌ CTRL+SHIFT shortcut not found: ${shortcutKey}`);
      }
    }

    // Handle Ctrl+A ONLY if it's NOT Ctrl+Shift+A (which was handled above)
    if (event.ctrlKey && !event.shiftKey && event.key.toLowerCase() === "a") {
      console.log('🎯 Ctrl+A detected - checking if shapes exist');
      if (this.scene.allShapes && this.scene.allShapes.length > 0) {
        console.log('🎯 Shapes exist, calling selectAllShapes');
        this.selectAllShapes();
        console.log("✅ Executed shortcut: CTRL+A (Select All)");
      } else {
        console.log('❌ No shapes to select');
      }
      return;
    }
    
    // Track keystroke for analytics
    if (this.scene.analyticsManager) {
      this.scene.analyticsManager.trackKeystrokes();
    }

    // Check for Ctrl+Shift+] (bring to front)
    if (event.ctrlKey && event.shiftKey && (event.key === ']' || event.key === '}')) {
      event.preventDefault();
      event.stopPropagation();
      console.log("Executing shortcut: Ctrl+Shift+] (Bring to Front)");
      this.scene.bringSelectionToFront?.();
      this.trackShortcutUsed("ctrl+shift+]");
      return;
    }

    // Check for Ctrl+Shift+[ (send to back)
    if (event.ctrlKey && event.shiftKey && (event.key === '[' || event.key === '{')) {
      event.preventDefault();
      event.stopPropagation();
      console.log("Executing shortcut: Ctrl+Shift+[ (Send to Back)");
      this.scene.sendSelectionToBack?.();
      this.trackShortcutUsed("ctrl+shift+[");
      return;
    }
  
    // Check if shortcuts are enabled (allow in practice mode or when goMessageShown)
    if (this.scene.currentRound !== 'practice' && !this.scene.goMessageShown) {
      console.log('❌ Sequential shortcuts blocked - not in practice mode and GO! not shown');
      return;
    }
    
    // Handle sequential shortcuts (original system) - Alt sequences  
    const pressed = event.key ? event.key.toLowerCase() : '';
    
    // For Alt key, start fresh sequence
    if (pressed === 'alt') {
      console.log('Starting Alt sequence');
      this.shortcutSequence = ['alt']; // Clear and start fresh
    }
    // Add other keys (not modifiers, not Ctrl combinations)
    else if (pressed && !["meta", "control", "shift"].includes(pressed) && !event.ctrlKey) {
      // Prevent key repeat duplicates
      const lastKey = this.shortcutSequence[this.shortcutSequence.length - 1];
      if (lastKey !== pressed) {
      console.log(`Adding key to sequence: ${pressed}`);
      this.shortcutSequence.push(pressed);
      }
    }
  
    // Log current sequence for debugging
    console.log("Current sequence:", this.shortcutSequence.join(","));
  
    // Get current shortcuts
    const shortcuts = this.getShortcuts();
    const seqStr = this.shortcutSequence.join(",");
    
    // Check if current sequence matches any shortcut
    if (shortcuts[seqStr]) {

      
      console.log(`Executing shortcut: ${seqStr}`);
      shortcuts[seqStr]();
      this.trackShortcutUsed(seqStr);
      
      // Show success notification for shortcut execution
      const description = this.getShortcutDescription(seqStr);
      this.scene.showCenterNotification(`✅ ${description}`, 'success', 1500);
      
      this.shortcutSequence = [];
      return;
    }
    
    // Check if it's a valid prefix of any shortcut
    const validPrefix = Object.keys(shortcuts).some(k => k.startsWith(seqStr));
    console.log(`Sequence is ${validPrefix ? 'a valid' : 'not a valid'} prefix`);
    
    // If not a valid prefix and we've typed at least 2 keys, reset
    if (!validPrefix && this.shortcutSequence.length >= 2) {
      console.log("Invalid sequence, resetting...");
      this.shortcutSequence = [];
    }
    
    // Auto-reset sequence after timeout
    if (this.sequenceTimer) {
      this.sequenceTimer.remove();
    }
    
    this.sequenceTimer = this.scene.time.delayedCall(3000, () => {
      this.shortcutSequence = [];
    });
  }

  // Track shortcut usage with analytics
  trackShortcutUsed(shortcutKey) {
    // Track in scene's analyticsData if analyticsManager doesn't exist
    if (this.scene.analyticsData && this.scene.analyticsData.shortcutsUsed) {
      if (!this.scene.analyticsData.shortcutsUsed[shortcutKey]) {
        this.scene.analyticsData.shortcutsUsed[shortcutKey] = 0;
      }
      this.scene.analyticsData.shortcutsUsed[shortcutKey]++;
      console.log(`📊 Shortcut tracked: ${shortcutKey} = ${this.scene.analyticsData.shortcutsUsed[shortcutKey]}`);
    }
    
    // Also try to use analyticsManager if it exists
    if (this.scene.analyticsManager) {
      this.scene.analyticsManager.trackShortcutUsed(shortcutKey, shortcutKey);
    }
  }

  // Create shortcuts popup content
  createShortcutsPopupContent() {
    const descriptions = this.getShortcutDescriptions();
    return "SHORTCUTS TO PRACTICE:\n\n" + descriptions.join("\n");
  }

  // ==============================
  // SHAPE ALIGNMENT METHODS (moved from base scene)
  // ==============================

  alignLeft() {
    if (this.scene.selectedShapes.length === 0) return;
    
    const leftmost = Math.min(...this.scene.selectedShapes.map(s => s.x - s.displayWidth / 2));
    this.scene.selectedShapes.forEach(shape => {
      shape.x = leftmost + shape.displayWidth / 2;
    });
    
    this.scene.trackKeystrokes();
    this.trackShortcutUsed("alt,h,g,a,l");
  }

  alignRight() {
    console.log('🔧 alignRight() called with', this.scene.selectedShapes.length, 'selected shapes');
    if (this.scene.selectedShapes.length === 0) {
      console.log('❌ No shapes selected - alignRight doing nothing');
      return;
    }
    
    // Find the rightmost edge of all selected shapes
    const rightmost = Math.max(...this.scene.selectedShapes.map(s => s.x + s.displayWidth / 2));
    console.log('🔧 Rightmost edge found at:', rightmost);
    
    // Align all shapes to the rightmost edge
    this.scene.selectedShapes.forEach((shape, index) => {
      const newX = rightmost - shape.displayWidth / 2;
      console.log(`🔧 Shape ${index}: old x=${shape.x}, new x=${newX}, displayWidth=${shape.displayWidth}`);
      shape.x = newX;
    });
    
    this.scene.trackKeystrokes();
    this.trackShortcutUsed("alt,h,g,a,r");
    console.log('✅ alignRight completed, shortcut tracked');
  }

  alignTop() {
    if (this.scene.selectedShapes.length === 0) return;
    
    const topmost = Math.min(...this.scene.selectedShapes.map(s => s.y - s.displayHeight / 2));
    this.scene.selectedShapes.forEach(shape => {
      shape.y = topmost + shape.displayHeight / 2;
    });
    
    this.scene.trackKeystrokes();
    this.trackShortcutUsed("alt,h,g,a,t");
  }

  alignBottom() {
    if (this.scene.selectedShapes.length === 0) return;
    
    const bottommost = Math.max(...this.scene.selectedShapes.map(s => s.y + s.displayHeight / 2));
    this.scene.selectedShapes.forEach(shape => {
      shape.y = bottommost - shape.displayHeight / 2;
    });
    
    this.scene.trackKeystrokes();
    this.trackShortcutUsed("alt,h,g,a,b");
  }

  alignCenterHorizontally() {
    if (this.scene.selectedShapes.length === 0) return;
    
    const centerX = this.scene.cameras.main.width / 2;
    this.scene.selectedShapes.forEach(shape => {
      shape.x = centerX;
    });
    
    this.scene.trackKeystrokes();
    this.trackShortcutUsed("alt,h,g,a,c");
  }

  alignCenterVertically() {
    if (this.scene.selectedShapes.length === 0) return;
    
    const centerY = this.scene.cameras.main.height / 2;
    this.scene.selectedShapes.forEach(shape => {
      shape.y = centerY;
    });
    
    this.scene.trackKeystrokes();
    this.trackShortcutUsed("alt,h,g,a,m");
  }

  selectAllShapes() {
    console.log('🔧 selectAllShapes() called - clearing selection first');
    console.log('🔧 Current game state:', this.scene.gameState, 'Input enabled:', this.scene.inputEnabled);
    console.log('🔧 Available shapes:', this.scene.allShapes ? this.scene.allShapes.length : 'undefined');
    
    this.scene.clearSelection();
    console.log('🔧 Selecting', this.scene.allShapes.length, 'shapes');
    
    this.scene.allShapes.forEach((shape, index) => {
      this.scene.selectedShapes.push(shape);
      shape.setStrokeStyle(2, 0xff0000);  // Add the visual selection indicator
      console.log(`🔧 Selected shape ${index}: x=${shape.x}, y=${shape.y}`);
    });
    console.log('✅ selectAllShapes completed -', this.scene.selectedShapes.length, 'shapes selected');
    
    // Track the shortcut usage
    this.trackShortcutUsed('ctrl,a');
  }

  // Clean up timers and listeners
  destroy() {
    if (this.sequenceTimer) {
      this.sequenceTimer.remove();
      this.sequenceTimer = null;
    }
    this.shortcutSequence = [];
    
    // Remove keyboard listener if needed
    if (this.scene.input && this.scene.input.keyboard) {
      this.scene.input.keyboard.removeAllListeners('keydown');
    }
  }

  // ==============================
  // SHAPE ALIGNMENT METHODS
  // ==============================

  alignShapes() {
    try {
      // This would contain the actual alignment logic
      // For now, just show a message
      this.scene.showCenterNotification('Shapes aligned!', '#00ff00');
      
      // Update score
      this.scene.score += 10;
      if (this.scene.scoreText) {
        this.scene.scoreText.setText(`Score: ${this.scene.score}`);
      }
      
    } catch (error) {
      console.error('Error aligning shapes:', error);
    }
  }

  // ==============================
  // DISTRIBUTION METHODS
  // ==============================

  distributeHorizontally() {
    if (this.scene.selectedShapes.length < 3) return;
    
    const sortedShapes = [...this.scene.selectedShapes].sort((a, b) => a.x - b.x);
    const firstX = sortedShapes[0].x;
    const lastX = sortedShapes[sortedShapes.length - 1].x;
    const spacing = (lastX - firstX) / (sortedShapes.length - 1);
    
    sortedShapes.forEach((shape, index) => {
      shape.x = firstX + (spacing * index);
    });
    
    this.scene.trackKeystrokes();
    this.scene.trackShortcutUsed("alt,h,g,a,h");
  }

  distributeVertically() {
    if (this.scene.selectedShapes.length < 3) return;
    
    const sortedShapes = [...this.scene.selectedShapes].sort((a, b) => a.y - b.y);
    const firstY = sortedShapes[0].y;
    const lastY = sortedShapes[sortedShapes.length - 1].y;
    const spacing = (lastY - firstY) / (sortedShapes.length - 1);
    
    sortedShapes.forEach((shape, index) => {
      shape.y = firstY + (spacing * index);
    });
    
    this.scene.trackKeystrokes();
    this.trackShortcutUsed("alt,h,g,a,v");
  }

  // Align shapes to center horizontally
  alignCenterHorizontally() {
    if (this.scene.selectedShapes.length === 0) return;
    
    const avgX = this.scene.selectedShapes.reduce((sum, s) => sum + s.x, 0) / this.scene.selectedShapes.length;
    this.scene.selectedShapes.forEach(shape => {
      shape.x = avgX;
    });
    
    this.scene.analyticsManager.trackKeystrokes();
    this.trackShortcutUsed("alt,h,g,a,c");
  }

  // Align shapes to center vertically
  alignCenterVertically() {
    if (this.scene.selectedShapes.length === 0) return;
    
    const avgY = this.scene.selectedShapes.reduce((sum, s) => sum + s.y, 0) / this.scene.selectedShapes.length;
    this.scene.selectedShapes.forEach(shape => {
      shape.y = avgY;
    });
    
    this.scene.analyticsManager.trackKeystrokes();
    this.trackShortcutUsed("alt,h,g,a,m");
  }

  // ==============================
  // ALIGNMENT METHODS
  // ==============================

  alignMiddle() {
    if (this.scene.selectedShapes.length === 0) return;
    
    const centerY = this.scene.selectedShapes.reduce((sum, s) => sum + s.y, 0) / this.scene.selectedShapes.length;
    this.scene.selectedShapes.forEach(shape => {
      shape.y = centerY;
    });
    
    this.scene.trackKeystrokes();
    this.scene.trackShortcutUsed("alt,h,g,a,m");
  }

  // ==============================
  // SHORTCUT DETECTION & HANDLING
  // ==============================

  checkShortcuts(event) {
    try {
      // This is a simplified shortcut detection
      // In the actual game, you'd have more sophisticated shortcut tracking
      const key = event.key.toLowerCase();
      
      // Example shortcut detection
      if (key === 'a' && event.altKey) {
        this.handleShortcut('align');
      }
      
    } catch (error) {
      console.error('Error checking shortcuts:', error);
    }
  }

  handleShortcut(shortcutType) {
    try {
      if (this.scene.analyticsData) {
        this.scene.analyticsData.shortcutsUsed++;
        this.scene.analyticsData.successfulShortcuts++;
      }
      
      // Execute the shortcut
      switch (shortcutType) {
        case 'align':
          this.scene.alignShapes();
          break;
        default:
          break;
      }
      
    } catch (error) {
      console.error('Error handling shortcut:', error);
    }
  }

  // ==============================
  // ALIGNMENT METHODS
  // ==============================

  alignLeft() {
    if (this.scene.selectedShapes.length === 0) return;
    
    const leftmost = Math.min(...this.scene.selectedShapes.map(s => s.x - s.displayWidth / 2));
    this.scene.selectedShapes.forEach(shape => {
      shape.x = leftmost + shape.displayWidth / 2;
    });
    
    this.scene.trackKeystrokes();
    this.scene.trackShortcutUsed("alt,h,g,a,l");
  }

  alignRight() {
    console.log('🔧 alignRight() called with', this.scene.selectedShapes.length, 'selected shapes');
    if (this.scene.selectedShapes.length === 0) {
      console.log('❌ No shapes selected - alignRight doing nothing');
      return;
    }
    
    const rightmost = Math.max(...this.scene.selectedShapes.map(s => s.x + s.displayWidth / 2));
    console.log('🔧 Aligning to rightmost edge:', rightmost);
    
    this.scene.selectedShapes.forEach((shape, index) => {
      const oldX = shape.x;
      shape.x = rightmost - shape.displayWidth / 2;
      console.log(`🔧 Shape ${index}: moved from x=${oldX} to x=${shape.x}`);
    });
    
    this.scene.trackKeystrokes();
    this.scene.trackShortcutUsed("alt,h,g,a,r");
    console.log('✅ alignRight completed, shortcut tracked');
  }

  alignTop() {
    if (this.scene.selectedShapes.length === 0) return;
    
    const topmost = Math.min(...this.scene.selectedShapes.map(s => s.y - s.displayHeight / 2));
    this.scene.selectedShapes.forEach(shape => {
      shape.y = topmost + shape.displayHeight / 2;
    });
    
    this.scene.trackKeystrokes();
    this.scene.trackShortcutUsed("alt,h,g,a,t");
  }

  alignBottom() {
    if (this.scene.selectedShapes.length === 0) return;
    
    const bottommost = Math.max(...this.scene.selectedShapes.map(s => s.y + s.displayHeight / 2));
    this.scene.selectedShapes.forEach(shape => {
      shape.y = bottommost - shape.displayHeight / 2;
    });
    
    this.scene.trackKeystrokes();
    this.scene.trackShortcutUsed("alt,h,g,a,b");
  }

  alignCenterHorizontally() {
    if (this.scene.selectedShapes.length === 0) return;
    
    const avgX = this.scene.selectedShapes.reduce((sum, s) => sum + s.x, 0) / this.scene.selectedShapes.length;
    this.scene.selectedShapes.forEach(shape => {
      shape.x = avgX;
    });
    
    this.scene.trackKeystrokes();
    this.scene.trackShortcutUsed("alt,h,g,a,c");
  }

  alignCenterVertically() {
    if (this.scene.selectedShapes.length === 0) return;
    
    const avgY = this.scene.selectedShapes.reduce((sum, s) => sum + s.y, 0) / this.scene.selectedShapes.length;
    this.scene.selectedShapes.forEach(shape => {
      shape.y = avgY;
    });
    
    this.scene.trackKeystrokes();
    this.scene.trackShortcutUsed("alt,h,g,a,m");
  }


}

export default ShortcutManager;

